/**
 * Navigation routing logic
 * Determines user navigation paths based on persona, KYC status, and onboarding state
 */

import {
  EntityTypeEnum,
  KYCStatus,
  Persona,
  SVPCategory,
  SpvStatus,
} from "@funded-here-interface/common/src/constant/enum";
import { getThanksPageUrl } from "@funded-here-interface/investor-onboarding/src/Screens/ThanksPage/ThanksPageUtils";
import { ThanksPageType } from "@funded-here-interface/investor-onboarding/src/constants/enums";
import { UserDetail, PersonaType } from "./navigation/types";
import {
  checkRegtankResponseRedirect,
  checkEkycCompletion,
  checkAuthRepEkycCompletion,
  hasCreditLimit,
  checkPendingResubmission,
  checkPendingApproval,
  isNewUser,
  isApprovedAndActive,
  hasEmailHistory,
} from "./navigation/guards";
import { NAVIGATION_PATHS } from "./navigation/constants";

/**
 * Main router function - maps persona to appropriate path resolver
 */
export const roleToPath = (
  userDetail: UserDetail,
  path?: string
): string | undefined => {
  const personaPathMap: Record<string, () => string | undefined> = {
    [Persona.INVESTOR]: () => getInvestorPath(userDetail, path),
    [Persona.DISTRIBUTOR]: () => getDistributorPath(userDetail),
    [Persona.MERCHANT]: () => getMerchantPath(userDetail),
    [Persona.MERCHANT_SPV]: () => getMerchantSpvPath(userDetail),
    [Persona.AUTH_REP]: () => getAuthRepPath(userDetail, path),
    [Persona.MERCHANT_SHAREHOLDER]: () =>
      getMerchantShareholderPath(userDetail),
    [Persona.MERCHANT_DIRECTOR]: () => getMerchantDirectorPath(userDetail),
    [SVPCategory.ISPV]: () => getIspvFundingSpvPath(userDetail),
    [Persona.ISSUER_DIRECTOR]: () => getIssuerDirectorPath(userDetail),
    [SVPCategory.FUNDINGSPV]: () => getIspvFundingSpvPath(userDetail),
    [Persona.INVESTOR_SHAREHOLDER]: () =>
      getInvestorShareholderPath(userDetail),
    [Persona.INVESTOR_DIRECTOR]: () => getInvestorDirectorPath(userDetail),
  };

  const getPath =
    userDetail?.pendingOnBoardingAs === null
      ? () => navigateBasedOnCompletedOnboarding(userDetail)
      : personaPathMap[userDetail?.pendingOnBoardingAs as PersonaType];

  return getPath?.();
};

/**
 * INVESTOR path resolution
 */
const getInvestorPath = (
  userDetail: UserDetail,
  path?: string
): string | undefined => {
  // Check Regtank response redirect first
  const regtankPath = checkRegtankResponseRedirect(userDetail, path);
  if (regtankPath) return regtankPath;

  // Check unqualified investor
  if (userDetail?.unqualifiedInvestor === true) {
    return NAVIGATION_PATHS.UNQUALIFIED_INVESTOR;
  }

  // New user - no organization yet
  if (isNewUser(userDetail)) {
    if (
      userDetail?.entityType === EntityTypeEnum.INDIVIDUAL ||
      userDetail?.investorCategory?.entityType === EntityTypeEnum.INDIVIDUAL
    ) {
      return NAVIGATION_PATHS.IND_BIZ_PROFILE;
    }

    if (
      userDetail?.entityType === EntityTypeEnum.INSTITUTION ||
      userDetail?.investorCategory?.entityType === EntityTypeEnum.INSTITUTION
    ) {
      return NAVIGATION_PATHS.BIZ_PROFILE;
    }
  }

  // Existing user with organization
  if (!isNewUser(userDetail)) {
    // Individual investor flow
    if (userDetail?.entityType === EntityTypeEnum.INDIVIDUAL) {
      const ekycPath = checkEkycCompletion(userDetail);
      if (ekycPath) return ekycPath;

      const resubmissionPath = checkPendingResubmission(
        userDetail,
        NAVIGATION_PATHS.IND_BIZ_PROFILE
      );
      if (resubmissionPath) return resubmissionPath;

      if (!userDetail?.declarationAISubmitted) {
        return NAVIGATION_PATHS.DECLARATION_AI;
      }

      if (userDetail?.kycStatus === KYCStatus.PENDING_APPROVAL) {
        if (hasEmailHistory(userDetail)) {
          return getThanksPageUrl(ThanksPageType.RESUBMITTED_IND_INVESTOR);
        }
        if (userDetail?.declarationAISubmitted) {
          return getThanksPageUrl(ThanksPageType.AGREEMENT);
        }
        return getThanksPageUrl(ThanksPageType.ONBOARDING);
      }
    }

    // Institution investor flow
    if (userDetail?.entityType === EntityTypeEnum.INSTITUTION) {
      const authRepEkycPath = checkAuthRepEkycCompletion(userDetail);
      if (authRepEkycPath) return authRepEkycPath;

      const resubmissionPath = checkPendingResubmission(
        userDetail,
        NAVIGATION_PATHS.BIZ_PROFILE
      );
      if (resubmissionPath) return resubmissionPath;
      if (userDetail?.kycStatus === KYCStatus.PENDING_APPROVAL) {
        // If there's email history, this is a resubmission
        if (hasEmailHistory(userDetail)) {
          return getThanksPageUrl(ThanksPageType.RESUBMITTED);
        }
        return getThanksPageUrl(ThanksPageType.ONBOARDING);
      }

      if (userDetail?.kycStatus === KYCStatus.APPROVED) {
        if (!userDetail.allShareHoldersDirectorsOnBoarded) {
          return NAVIGATION_PATHS.PENDING_SHAREHOLDER_ONBOARD;
        } else if (
          userDetail?.allShareHoldersDirectorsOnBoarded &&
          !userDetail?.declarationAISubmitted
        ) {
          return getThanksPageUrl(ThanksPageType.PENDING_AGREEMENT);
        } else if (
          userDetail?.isAuthRep &&
          userDetail?.allShareHoldersDirectorsOnBoarded
        ) {
          return NAVIGATION_PATHS.DECLARATION_AI;
        }
      }
    }
  }

  return undefined;
};

/**
 * DISTRIBUTOR path resolution
 */
const getDistributorPath = (userDetail: UserDetail): string | undefined => {
  if (userDetail?.orgRole?.length === 0) {
    return NAVIGATION_PATHS.SUPPLIER_BIZ_PROFILE;
  }

  if (userDetail?.orgRole?.length !== 0) {
    const pendingPath = checkPendingApproval(userDetail);
    if (pendingPath) return pendingPath;

    const resubmissionPath = checkPendingResubmission(
      userDetail,
      NAVIGATION_PATHS.SUPPLIER_BIZ_PROFILE
    );
    if (resubmissionPath) return resubmissionPath;

    if (userDetail?.kycStatus === KYCStatus.APPROVED) {
      return NAVIGATION_PATHS.UPLOAD_INVOICE;
    }
  }

  return undefined;
};

/**
 * AUTH_REP path resolution
 */
const getAuthRepPath = (
  userDetail: UserDetail,
  path?: string
): string | undefined => {
  const regtankPath = checkRegtankResponseRedirect(userDetail, path);
  if (regtankPath) return regtankPath;

  const role = userDetail?.orgRole?.[0]?.role?.name;
  const userEntityType = userDetail?.entityType;

  // Role-based agreement path mapping
  const agreementPathByRole: Partial<Record<string, string>> = {
    [Persona.INVESTOR]: NAVIGATION_PATHS.DECLARATION_AI,
    [Persona.ISPV]: NAVIGATION_PATHS.ISSUER_AGREEMENT,
    [Persona.MERCHANT]: NAVIGATION_PATHS.MERCHANT_MEMBERSHIP_AGREEMENT,
  };

  const targetPath = agreementPathByRole[role as string];
  const hasSeenSignedAgreement =
    userDetail?.didMetaData !== null && userDetail?.didMetaData !== undefined
      ? (userDetail?.didMetaData?.hasSeenSignedAgreement ?? false)
      : false;
  if (hasSeenSignedAgreement) return undefined;

  // Investor individual has different condition
  if (
    role === Persona.INVESTOR &&
    userEntityType === EntityTypeEnum.INDIVIDUAL
  ) {
    return !userDetail?.declarationAISubmitted ? targetPath : undefined;
  }

  // All other roles require shareholders/directors onboarded
  return userDetail?.allShareHoldersDirectorsOnBoarded ? targetPath : undefined;
};

/**
 * MERCHANT path resolution
 */
const getMerchantPath = (userDetail: UserDetail): string | undefined => {
  const creditLimit = hasCreditLimit(userDetail);
  const regtankPath = checkRegtankResponseRedirect(userDetail);
  if (regtankPath) return regtankPath;

  if (isNewUser(userDetail)) {
    return NAVIGATION_PATHS.NEW_MERCHANT_ONBOARD;
  }

  // Existing merchant
  const ekycPath = checkEkycCompletion(userDetail);
  if (ekycPath) return ekycPath;

  const resubmissionPath = checkPendingResubmission(
    userDetail,
    NAVIGATION_PATHS.NEW_MERCHANT_ONBOARD
  );
  if (resubmissionPath) return resubmissionPath;

  if (userDetail?.kycStatus === KYCStatus.PENDING_APPROVAL || !creditLimit) {
    // If there's email history, this is a resubmission
    if (hasEmailHistory(userDetail)) {
      return getThanksPageUrl(ThanksPageType.RESUBMITTED);
    }
    return getThanksPageUrl(ThanksPageType.ONBOARDING);
  }

  // Approved merchant with credit limit
  if (creditLimit && userDetail?.kycStatus === KYCStatus.APPROVED) {
    if (!userDetail.allShareHoldersDirectorsOnBoarded) {
      return NAVIGATION_PATHS.PENDING_SHAREHOLDER_ONBOARD;
    } else if (
      userDetail?.allShareHoldersDirectorsOnBoarded &&
      !userDetail?.declarationAISubmitted
    ) {
      return getThanksPageUrl(ThanksPageType.PENDING_AGREEMENT);
    } else if (
      userDetail?.isAuthRep &&
      userDetail?.allShareHoldersDirectorsOnBoarded
    ) {
      return NAVIGATION_PATHS.MERCHANT_MEMBERSHIP_AGREEMENT;
    }
  }

  return undefined;
};

/**
 * MERCHANT_SPV path resolution
 */
const getMerchantSpvPath = (userDetail: UserDetail): string | undefined => {
  const creditLimit = hasCreditLimit(userDetail);
  const regtankPath = checkRegtankResponseRedirect(userDetail);
  if (regtankPath) return regtankPath;

  const authRepEkycPath = checkAuthRepEkycCompletion(userDetail);
  if (authRepEkycPath) return authRepEkycPath;

  const resubmissionPath = checkPendingResubmission(
    userDetail,
    NAVIGATION_PATHS.NEW_MERCHANT_ONBOARD
  );
  if (resubmissionPath) return resubmissionPath;

  if (userDetail?.kycStatus === KYCStatus.PENDING_APPROVAL || !creditLimit) {
    // If there's email history, this is a resubmission
    if (hasEmailHistory(userDetail)) {
      return getThanksPageUrl(ThanksPageType.RESUBMITTED);
    }
    return getThanksPageUrl(ThanksPageType.ONBOARDING);
  }

  // Approved SPV merchant with credit limit
  if (userDetail?.kycStatus === KYCStatus.APPROVED && creditLimit) {
    if (userDetail?.spvStatus === SpvStatus.PENDING_INITIATION) {
      if (!userDetail?.allDirectorsSignedResolutionSPV) {
        return NAVIGATION_PATHS.PENDING_SPV_BOARD_RESOLUTION;
      }
      return NAVIGATION_PATHS.SETUP_SPV;
    }

    if (userDetail?.spvStatus === SpvStatus.INITIATED) {
      return NAVIGATION_PATHS.PENDING_SPV;
    }

    if (userDetail?.spvStatus === SpvStatus.COMPLETED) {
      if (userDetail?.spvDetails?.kycStatus === KYCStatus.PENDING_APPROVAL) {
        return NAVIGATION_PATHS.SPV_KYC_PENDING;
      }

      if (!userDetail?.allShareHoldersDirectorsOnBoarded) {
        return NAVIGATION_PATHS.PENDING_SHAREHOLDER_ONBOARD;
      }

      if (
        userDetail?.allShareHoldersDirectorsOnBoarded &&
        userDetail?.isAuthRep
      ) {
        return NAVIGATION_PATHS.MERCHANT_MEMBERSHIP_AGREEMENT;
      }
    }
  }

  return undefined;
};

/**
 * MERCHANT_SHAREHOLDER path resolution
 */
const getMerchantShareholderPath = (
  userDetail: UserDetail
): string | undefined => {
  const regtankPath = checkRegtankResponseRedirect(userDetail);
  return regtankPath ?? NAVIGATION_PATHS.THANKS_PAGE;
};

/**
 * MERCHANT_DIRECTOR path resolution
 */
const getMerchantDirectorPath = (
  userDetail: UserDetail
): string | undefined => {
  const role = userDetail?.orgRole?.[0]?.role?.name;
  const regtankPath = checkRegtankResponseRedirect(userDetail);

  if (regtankPath) {
    return regtankPath;
  }

  if (role === Persona.MERCHANT_SPV) {
    if (!userDetail.orgSpvName && !userDetail?.signedBoardResolutionSpv) {
      return NAVIGATION_PATHS.DIRECTOR_BOARD_RESOLUTION_SPV;
    }

    if (userDetail.orgSpvName) {
      if (!userDetail?.spvDetails) {
        return NAVIGATION_PATHS.DIRECTOR_BOARD_RESOLUTION;
      }

      // Check if director is in SPV board
      for (const director of userDetail?.spvDetails?.boardOfDirectors ?? []) {
        if (userDetail?.email === director?.email) {
          if (
            userDetail?.spvDetails?.kycStatus === KYCStatus.PENDING_APPROVAL
          ) {
            return NAVIGATION_PATHS.SPV_KYC_PENDING;
          }
          return NAVIGATION_PATHS.DIRECTOR_BOARD_RESOLUTION;
        }
      }
    }
  }

  if (role === Persona.MERCHANT) {
    if (isApprovedAndActive(userDetail)) {
      return NAVIGATION_PATHS.DIRECTOR_BOARD_RESOLUTION;
    }
  }

  return undefined;
};

/**
 * INVESTOR_SHAREHOLDER path resolution
 */
const getInvestorShareholderPath = (
  userDetail: UserDetail
): string | undefined => {
  const regtankPath = checkRegtankResponseRedirect(userDetail);
  return regtankPath ?? NAVIGATION_PATHS.THANKS_PAGE;
};

/**
 * INVESTOR_DIRECTOR path resolution
 */
const getInvestorDirectorPath = (
  userDetail: UserDetail
): string | undefined => {
  const regtankPath = checkRegtankResponseRedirect(userDetail);

  if (regtankPath) {
    return regtankPath;
  }

  if (isApprovedAndActive(userDetail)) {
    return NAVIGATION_PATHS.DIRECTOR_BOARD_RESOLUTION;
  }

  return undefined;
};

/**
 * ISSUER_DIRECTOR path resolution
 */
const getIssuerDirectorPath = (userDetail: UserDetail): string | undefined => {
  const regtankPath = checkRegtankResponseRedirect(userDetail);

  if (regtankPath) {
    return regtankPath;
  }

  if (isApprovedAndActive(userDetail)) {
    return NAVIGATION_PATHS.ISSUER_BOARD_RESOLUTION;
  }

  return undefined;
};

/**
 * ISPV/FUNDINGSPV path resolution
 */
const getIspvFundingSpvPath = (userDetail: UserDetail): string | undefined => {
  if (isNewUser(userDetail)) {
    return NAVIGATION_PATHS.BIZ_PROFILE;
  }

  const regtankPath = checkRegtankResponseRedirect(userDetail);
  if (regtankPath) return regtankPath;

  if (
    !isNewUser(userDetail) &&
    userDetail?.kycStatus === KYCStatus.PENDING_APPROVAL
  ) {
    if (userDetail?.pendingOnBoardingAs === Persona.FUNDINGSPV) {
      return NAVIGATION_PATHS.THANKS_PAGE;
    }
  }

  const resubmissionPath = checkPendingResubmission(
    userDetail,
    NAVIGATION_PATHS.BIZ_PROFILE
  );
  if (resubmissionPath) return resubmissionPath;

  if (userDetail?.kycStatus === KYCStatus.PENDING_APPROVAL) {
    // If there's email history, this is a resubmission
    if (hasEmailHistory(userDetail)) {
      return getThanksPageUrl(ThanksPageType.RESUBMITTED);
    }
    return getThanksPageUrl(ThanksPageType.ONBOARDING);
  }

  if (!userDetail.allShareHoldersDirectorsOnBoarded) {
    return NAVIGATION_PATHS.PENDING_SHAREHOLDER_ONBOARD;
  } else if (
    userDetail?.allShareHoldersDirectorsOnBoarded &&
    !userDetail?.declarationAISubmitted
  ) {
    return getThanksPageUrl(ThanksPageType.PENDING_AGREEMENT);
  } else if (
    userDetail?.isAuthRep &&
    userDetail?.allShareHoldersDirectorsOnBoarded
  ) {
    return NAVIGATION_PATHS.ISSUER_AGREEMENT;
  }

  return undefined;
};

/**
 * Determines navigation path for completed onboarding
 * Handles business logic for users who have completed initial onboarding
 */
const navigateBasedOnCompletedOnboarding = (
  userDetail: UserDetail
): string | undefined => {
  const creditLimit = hasCreditLimit(userDetail);
  const role = userDetail?.orgRole?.[0]?.role?.name;

  // Investor - Individual
  if (
    role === Persona.INVESTOR &&
    userDetail?.entityType === EntityTypeEnum.INDIVIDUAL &&
    !userDetail?.isSuspended &&
    userDetail?.declarationAISubmitted
  ) {
    return NAVIGATION_PATHS.NOTE_OFFER_ROOM;
  }

  // Investor - Institution
  if (
    role === Persona.INVESTOR &&
    userDetail?.entityType === EntityTypeEnum.INSTITUTION &&
    !userDetail?.isSuspended &&
    userDetail?.declarationAISubmitted
  ) {
    return NAVIGATION_PATHS.NOTE_OFFER_ROOM;
  }

  // Merchant roles - authorization-based routing
  if (role === Persona.MERCHANT || role === Persona.MERCHANT_SPV) {
    /**
     * Merchant authorization combinations:
     * - AuthRep only: loan-agreement (can only view/sign agreements)
     * - Admin only: collaboration-proposal (can create loan applications)
     * - Admin + AuthRep: collaboration-proposal (full access)
     */
    if (userDetail?.isAuthRep && !userDetail?.isAdminAuthRep) {
      return NAVIGATION_PATHS.LOAN_AGREEMENT;
    }

    if (creditLimit && !userDetail?.isAuthRep) {
      return NAVIGATION_PATHS.COLLABORATION_PROPOSAL;
    }

    if (userDetail?.isAuthRep && userDetail?.isAdminAuthRep) {
      return NAVIGATION_PATHS.COLLABORATION_PROPOSAL;
    }
  }

  // ISPV/FundingSPV
  if (
    (role === SVPCategory.ISPV || role === SVPCategory.FUNDINGSPV) &&
    userDetail?.declarationAISubmitted
  ) {
    return NAVIGATION_PATHS.FUNDING_ISSUING_SPV_ONBOARD_COMPLETED;
  }

  // Distributor
  if (role === Persona.DISTRIBUTOR) {
    return NAVIGATION_PATHS.UPLOAD_INVOICE;
  }

  return undefined;
};
